import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { Link } from '@backstage/core-components';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import { PageHeader } from '../common/PageHeader';
import { BreadcrumbItem } from '../../types';

const DOCS_PATH_RE = /^\/docs\/([^/]+)\/([^/]+)\/([^/]+)/;

const useStyles = makeStyles({
  meta: {
    display: 'flex',
    gap: '24px',
    alignItems: 'flex-start',
  },
  metaColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  metaLabel: {
    fontSize: '11px',
    color: 'var(--arla-text-secondary-color)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: 600,
  },
  metaLink: {
    fontSize: '13px',
    color: 'var(--arla-breadcrumb-item-hover-color)',
    fontWeight: 500,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

interface InnerProps {
  namespace: string;
  kind: string;
  name: string;
}

const TechDocsPageHeaderInner = ({ namespace, kind, name }: InnerProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const [entity, setEntity] = useState<Entity | null>(null);

  useEffect(() => {
    catalogApi.getEntityByRef({ namespace, kind, name }).then(e => setEntity(e ?? null));
  }, [catalogApi, namespace, kind, name]);

  const title = entity?.metadata.title || name;
  const description = entity?.metadata.description;

  const rawOwner = entity?.spec?.owner as string | undefined;
  const ownerName = rawOwner?.split('/').pop() ?? rawOwner;
  const ownerKind = rawOwner?.includes(':') ? rawOwner.split(':')[0] : 'group';
  const ownerLink = ownerName ? `/catalog/${namespace}/${ownerKind}/${ownerName}` : undefined;

  const sourceLocation = entity?.metadata.annotations?.['backstage.io/source-location'];
  const githubUrl = sourceLocation?.startsWith('url:') ? sourceLocation.slice(4).trim() : undefined;

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Docs', path: '/docs' },
    { label: title },
  ];

  const extra = (ownerLink || githubUrl) ? (
    <div className={classes.meta}>
      {ownerLink && (
        <div className={classes.metaColumn}>
          <span className={classes.metaLabel}>Owner</span>
          <Link to={ownerLink} className={classes.metaLink}>{ownerName}</Link>
        </div>
      )}
      {githubUrl && (
        <div className={classes.metaColumn}>
          <span className={classes.metaLabel}>Source</span>
          <a href={githubUrl} target="_blank" rel="noopener noreferrer" className={classes.metaLink}>
            GitHub
          </a>
        </div>
      )}
    </div>
  ) : undefined;

  return (
    <PageHeader
      title={title}
      breadcrumbs={breadcrumbs}
      description={description}
      extra={extra}
      showDate={false}
    />
  );
};

export const TechDocsPageHeader = () => {
  const location = useLocation();
  const match = location.pathname.match(DOCS_PATH_RE);
  if (!match) return null;
  const [, namespace, kind, name] = match;
  return <TechDocsPageHeaderInner namespace={namespace} kind={kind} name={name} />;
};
